import { RequestResponseMessage } from '../requestresponse';
import { MessageLikeEvent, MessageRequestResponseProtocol } from './message';

interface MessageProxyRequest {
  functionName: string;
  args: any[];
}

type MessageProxyResponse = any;

interface MessageEventTarget {
  addEventListener<T>(key: string, listener: (event: T) => any, options?: { once: boolean }): void;
}

export type MessageProxyClientWrappedFunction<F extends Function> = F extends (
  ...args: infer A
) => infer R
  ? R extends Promise<any>
    ? F
    : (...args: A) => Promise<R>
  : never;

type MessageProxyClientInterface<T> = {
  [K in keyof T]: T[K] extends Function ? MessageProxyClientWrappedFunction<T[K]> : T[K];
};

type MethodKeys<T> = { [P in keyof T]: T[P] extends Function ? P : never }[keyof T];
type OmitNonMethods<T> = Pick<T, MethodKeys<T>>;
export type MessageProxyClient<T> = OmitNonMethods<MessageProxyClientInterface<T>>;

type MessageProxyRequestResponseMessage = RequestResponseMessage<
  MessageProxyRequest,
  MessageProxyResponse
>;

const MessageProxyProtocol =
  new MessageRequestResponseProtocol<MessageProxyRequestResponseMessage>();

/**
 * A message proxy server that exposes methods on a handler object via a
 * message channel listener. {@link createMessageProxyClientToIFrame} can be
 * used to set up the other side of the channel, allowing for methods on a
 * handler object within an iframe to be transparently called by code outside
 * that iframe.
 */
export class MessageProxyServer<T extends object> {
  private _messagePort: Promise<MessagePort>;
  private _handler: T | undefined = undefined;

  /**
   * Create a message proxy server. Synchronously adds a message event listener
   * to 'target' for communication with {@link createMessageProxyClientToIFrame}.
   * To avoid the possibility of missing messages and failing to initialize the
   * communication channel, make sure to create the message proxy server before
   * the page load event fires.
   *
   * @param target - An event target for 'message' events, such as 'window'.
   * @param key - A string key to avoid confusion between handshake messages for
   * different MessageProxyServers.
   */
  constructor(target: MessageEventTarget, key: string) {
    this._messagePort = this._receiveMessagePort(target, key);
  }

  /**
   * Register the proxied handler object and open the communication channel.
   * May only be called once.
   *
   * It's safe to register the handler object after the page load event has
   * fired, or after an arbitrary delay generally. This makes it possible to use
   * handler objects which are constructed asynchronously.
   */
  async registerHandler(handler: T): Promise<void> {
    if (this._handler !== undefined) {
      throw new Error(`Registering handler for a MessageProxyServer with an existing handler`);
    }
    this._handler = handler;

    const port = await this._messagePort;

    port.onmessage = (event: MessageLikeEvent) => {
      MessageProxyProtocol.handleMessage(event, async request => {
        const handlingFunction = handler[request.functionName as keyof T];
        if (typeof handlingFunction !== 'function') {
          return Promise.reject(
            `Specified function name ${request.functionName} is not a function`,
          );
        }

        const response = await Promise.resolve(handlingFunction.apply(handler, request.args));
        return [request, response];
      });
    };

    port.postMessage('ready');
  }

  private _receiveMessagePort(target: MessageEventTarget, key: string): Promise<MessagePort> {
    return new Promise(resolve => {
      target.addEventListener<MessageLikeEvent>('message', async event => {
        if (!event.data || event.data.action !== 'init' || event.data.key !== key) {
          return;
        }

        return resolve(event.ports[0]);
      });
    });
  }
}

type HasReadyState = {
  readyState: string;
};

type IFrameLike = MessageEventTarget & {
  contentDocument: HasReadyState | null;
  contentWindow: {
    postMessage(message: any, targetOrigin: string, transfer?: any[]): void;
    document?: HasReadyState | null;
  } | null;
};

/**
 * Create a message port to iframe-like element
 * @param iframe target iframe
 * @param key string key to ensure handshake post messages are encapsulated
 */
export function createMessageProxyClientToIFrame<T extends object>(
  iframe: IFrameLike,
  stub: T,
  key: string,
): Promise<MessageProxyClient<T>> {
  return new Promise((resolve, reject) => {
    const onLoad = () => {
      // Create a new channel that will be used to communicate with the iframe.
      // Note that we create a new channel every time the load event fires, and
      // only commit to a channel when we actually receive a 'ready' message on
      // that channel. This is necessary for non-'about:blank' iframes because
      // the load event may fire twice: once for the initial 'about:blank'
      // document, and once for the document the iframe is navigating to. The
      // channel we send to the 'about:blank' document will end up getting
      // silently discarded, since there's no code there to receive it.
      const channel = new MessageChannel();
      channel.port1.onmessage = (message: MessageEvent) => {
        if (message.data !== 'ready') {
          return reject(new Error('Received a non ready message'));
        }

        resolve(CreateMessageProxyClient(channel.port1, stub));
      };

      iframe.contentWindow!.postMessage(
        {
          action: 'init',
          key,
        },
        '*',
        [channel.port2],
      );
    };

    iframe.addEventListener('load', onLoad, {
      once: true,
    });
  });
}

function CreateMessageProxyClient<T extends object>(
  target: MessagePort,
  stub: T,
): MessageProxyClient<T> {
  const proxy: any = {};
  const stubMethods = stub as { [method: string]: Function | unknown };
  for (const k of Object.getOwnPropertyNames(Object.getPrototypeOf(stubMethods))) {
    if (typeof stubMethods[k] === 'function') {
      proxy[k] = proxyFunction(target, stubMethods[k] as Function);
    }
  }

  return proxy as MessageProxyClient<T>;
}

function proxyFunction<F extends Function>(
  target: MessagePort,
  f: F,
): MessageProxyClientWrappedFunction<F> {
  return async function (...args): Promise<any> {
    return MessageProxyProtocol.sendMessage(target, {
      functionName: f.name,
      args,
    });
  } as MessageProxyClientWrappedFunction<F>;
}
